import moment from 'moment';
import { Col, Row, notification, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { capitalize } from 'core/utils/strings';
import useBreadcrumb from 'hooks/useBreadcrumb';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    generateIrelandSadDraft,
    generateIrelandClearanceDraft,
    archiveDeclaration,
    unarchiveDeclaration,
    deleteDeclaration,
    listIcs2Declarations,
} from 'store/declarations/client';
import { Declaration, DeclarationAndAgent } from 'store/declarations/declaration';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import DeclarationTable from './declaration-table/DeclarationsTable';
import { ListPayload } from 'core/http/response';
import { DeclarationExternalEntity } from '../../store/declarations/enums/common/declaration-external-entity';
import DeclarationDashboardActionRequiredMessage from './declaration-table/components/DeclarationDashboardActionRequiredMessage';
import { GvmsReferenceData } from '../../store/declarations/uk/gvms-declaration';
import { getGvmsReferenceData } from '../declarations/uk/gvms/utils';
import BarcodeModal from './declaration-table/BarcodeModal';
import useDeclarations from '../../hooks/useDeclarations';
import ArchiveDeclarationModal from './declaration-table/components/ArchiveDeclarationModal';
import DeleteDeclarationModal from './declaration-table/components/DeleteDeclarationModal';
import UnarchiveDeclarationModal from './declaration-table/components/UnarchiveDeclarationModal';
import DocumentsModal from './declaration-table/components/DocumentsModal';
import { hasPermissions } from 'config/roles/hasPermissions';
import useSession from 'hooks/useSession';
import Notification from 'components/ui/base/notification/Notification';
import { getErrorMessage } from 'utils/getErrorMessage';
import PageHeader from 'components/new-ui/base/page-header/PageHeader';
import Button from 'components/new-ui/base/button/Button';
import DeclarationFiltersModal, { ModalFilters } from './declaration-table/DeclarationFiltersModal';
import RangePicker from 'components/new-ui/base/date-picker/RangePicker';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import useDeclarationsTable from './declaration-table/components/useDeclarationsTable';
import { StyledPageWrapper } from 'components/new-ui/base/wrappers/styles';
import { StyledSegmented } from 'components/new-ui/base/segmented/styles';
import DeclarationFilterTags from './declaration-table/components/DeclarationFilterTags';
import { getCreateDeclarationButtonTitle, getDeclarationCountryFlag, getDeclarationsDashboardPageTitle } from './utils';
import useIcs2GroupActions from './declaration-table/components/useIcs2GroupActions';
import ConsultEnsModalButton from '../declarations/common/consult-ens/ConsultEnsModalButton';

type ModalState =
    | { type: 'none' }
    | { type: 'filter' }
    | { type: 'archive'; ids: string[] }
    | { type: 'unarchive'; ids: string[] }
    | { type: 'delete'; id: string }
    | { type: 'barcode'; declaration: Declaration }
    | { type: 'print'; declarationId: string };

const DeclarationDashboard: FC = () => {
    const { country, type } = useParams<{
        country: DeclarationCountry;
        type: DeclarationInternalType;
    }>();

    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation('customs_declarations');
    const { setBreadcrumbRoutes } = useBreadcrumb();
    const { toggleCoreTemplateStatus } = useDeclarations();
    const { userInfo } = useSession();

    const [modal, setModal] = useState<ModalState>({ type: 'none' });
    const [gvmsReferenceData, setGvmsReferenceData] = useState<GvmsReferenceData | undefined>(undefined);

    const canCreateDeclarations = hasPermissions(userInfo, 'declarations', 'CREATE');
    const canArchiveDeclarations = hasPermissions(userInfo, 'declarations', 'ARCHIVE');
    const canDeleteDeclarations = hasPermissions(userInfo, 'declarations', 'DELETE');
    const entity = type === DeclarationInternalType.GVMS ? 'record' : 'declaration';
    const pageTitle = getDeclarationsDashboardPageTitle(type, country, t);
    const pageIcon = getDeclarationCountryFlag(country);
    const createButtonTitle = getCreateDeclarationButtonTitle(type, country);
    const isEnsDashboard = country === DeclarationCountry.EU && type === DeclarationInternalType.ENS;

    const declarationExternalEntityParams = useMemo(() => {
        const declarationParams: {
            declarationExternalEntity: DeclarationExternalEntity[];
            declarationInternalType: DeclarationInternalType[];
        } = {
            declarationInternalType: type ? [type] : [],
            declarationExternalEntity: [],
        };

        if (country === DeclarationCountry.IRELAND) {
            declarationParams.declarationExternalEntity.push(DeclarationExternalEntity.REVENUE);
        } else if (country === DeclarationCountry.UK) {
            if (type === DeclarationInternalType.IMPORT) {
                declarationParams.declarationInternalType.push(DeclarationInternalType.IMPORT_NEW);
            }

            if (type === DeclarationInternalType.GVMS) {
                declarationParams.declarationExternalEntity.push(DeclarationExternalEntity.GVMS);
            } else if (type === DeclarationInternalType.NCTS) {
                declarationParams.declarationExternalEntity.push(DeclarationExternalEntity.HMRC);
            } else {
                declarationParams.declarationExternalEntity.push(DeclarationExternalEntity.CDS);
            }
        } else if (country === DeclarationCountry.EU) {
            declarationParams.declarationInternalType.push(DeclarationInternalType.ENS);
            declarationParams.declarationExternalEntity.push(DeclarationExternalEntity.ICS2);
        }

        return declarationParams;
    }, [type, country]);

    const {
        queryInputVal,
        appliedParams,
        dates,
        tags,
        clearFilter,
        clearAll,
        setDates,
        setPage,
        setQueryInputVal,
        setArchived,
        setIsCoreTemplate,
        data: declarations,
        isLoadingDeclarations,
        refetchDeclarations,
        handleTableChange,
        handleFiltersChange,
        selectedIds,
        rowSelection,
        handleExportDeclarationsCSV,
        isExportingCSV,
    } = useDeclarationsTable(undefined, declarationExternalEntityParams, {
        listFn: type === DeclarationInternalType.ENS ? (listIcs2Declarations as any) : undefined,
    });

    const { size, page, archived, declarationStatus, declarationType } = appliedParams;

    const isActiveDeclarationsFilter = !Boolean(appliedParams.archived || appliedParams.isCoreTemplate);

    const declarationFilters: ModalFilters = {
        query: queryInputVal,
        declarationStatus,
        declarationType,
    };

    const closeModal = useCallback(() => setModal({ type: 'none' }), []);

    const { groupActions, groupActionModals } = useIcs2GroupActions({
        onDone: () => refetchDeclarations(appliedParams),
    });

    const handleOpenBarcodeModal = (declaration: Declaration | undefined) => {
        if (declaration) setModal({ type: 'barcode', declaration });
    };

    const refreshDeclarations = useCallback(() => {
        refetchDeclarations(appliedParams);
    }, [appliedParams, refetchDeclarations]);

    const handlePrintButtonClick = async (declarationId: string | undefined) => {
        if (!declarationId) return;

        try {
            await Promise.all([generateIrelandSadDraft(declarationId), generateIrelandClearanceDraft(declarationId)]);

            setModal({ type: 'print', declarationId });
        } catch (err) {
            const message = getErrorMessage(err);
            Notification({
                type: 'error',
                placement: 'top',
                messageTitle: 'Error',
                description: message || 'Could not detect selected declaration id. Please try again.',
            });
        }
    };

    const handleDuplicate = async (declaration: Declaration) => {
        navigate(`/invoice-upload/${country}/${type}/job/${declaration.jobId}`, {
            state: { declaration: declaration },
        });
    };

    const handleDeleteButtonClick = (deleteId: string | undefined) => {
        if (deleteId) setModal({ type: 'delete', id: deleteId });
    };

    const handleToggleCoreTemplateStatus = useCallback(
        async (declaration: Declaration) => {
            if (!declaration?.id) return;

            const notificationType = declaration?.isCoreTemplate ? 'removeAsCoreTemplate' : 'setAsCoreTemplate';

            await toggleCoreTemplateStatus(declaration.id, {
                notificationType,
            });
            refreshDeclarations();
        },
        [refreshDeclarations, toggleCoreTemplateStatus]
    );

    const handleDeleteDeclaration = () => {
        if (modal.type !== 'delete')
            return notification.error({ message: 'Something went wrong, please try again later!' });

        deleteDeclaration(modal.id)
            .then(() => {
                notification.success({ message: 'Declaration deleted successfully!' });
                refreshDeclarations();
            })
            .catch(() => {
                notification.error({ message: 'Something went wrong, please try again later!' });
            })
            .finally(() => {
                closeModal();
            });
    };

    const handleArchive = (ids: string[]) => setModal({ type: 'archive', ids });

    const handleUnarchive = (ids: string[]) => setModal({ type: 'unarchive', ids });

    const archiveDeclarations = async () => {
        if (modal.type !== 'archive') return;
        const ids = modal.ids;
        closeModal();
        Promise.all(ids.map((id) => archiveDeclaration(id)))
            .then(() => {
                refreshDeclarations();
                Notification({
                    type: 'success',
                    messageTitle: 'Operation Successful',
                    description: `${capitalize(entity)} archived successfully!`,
                    placement: 'top',
                });
            })
            .catch((err) => {
                const errorMessage = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Operation Failed',
                    description: errorMessage || `Could not archive the ${entity}!`,
                    placement: 'top',
                });
            });
    };
    const unarchiveDeclarations = async () => {
        if (modal.type !== 'unarchive') return;
        const ids = modal.ids;
        closeModal();
        Promise.all(ids.map((id) => unarchiveDeclaration(id)))
            .then((res) => {
                refreshDeclarations();
                Notification({
                    type: 'success',
                    messageTitle: 'Operation Successful',
                    description: `${capitalize(entity)} unarchived successfully!`,
                    placement: 'top',
                });
            })
            .catch((err) => {
                const errorMessage = getErrorMessage(err);
                Notification({
                    type: 'error',
                    messageTitle: 'Operation Failed',
                    description: errorMessage || `Could not unarchive the ${entity}!`,
                    placement: 'top',
                });
            });
    };

    useEffect(() => {
        if (
            country === DeclarationCountry.UK &&
            type === DeclarationInternalType.GVMS &&
            gvmsReferenceData === undefined
        )
            getGvmsReferenceData().then(setGvmsReferenceData);
    }, [country, type, gvmsReferenceData]);

    useEffect(() => {
        setBreadcrumbRoutes([
            {
                breadcrumbName: 'Customs Declarations',
                path: '',
            },
            {
                breadcrumbName: t(`custom${country}`),
                path: '',
            },
            {
                breadcrumbName: t(`${type}${country}`),
                path: '',
            },
        ]);
    }, [country, setBreadcrumbRoutes, t, type]);

    // Clear the filters when we switch between differnt dashboards
    useEffect(() => {
        return () => {
            clearAll();
        };
    }, [clearAll, location.pathname]);

    return (
        <StyledPageWrapper>
            <Space style={{ width: '100%' }} direction="vertical" size={24}>
                <PageHeader
                    icon={pageIcon}
                    title={pageTitle}
                    additionalContent={
                        <>
                            {/* NOTE: There are some issues with this functionalty. Created #7221 to followup */}
                            {/* <AddFavouriteButton /> */}
                        </>
                    }
                    sidebar={
                        <Space size={16}>
                            {!isActiveDeclarationsFilter &&
                                ((type === DeclarationInternalType.EXPORT && country === DeclarationCountry.IRELAND) ||
                                    type === DeclarationInternalType.GVMS ||
                                    type === DeclarationInternalType.NCTS) && (
                                    <DeclarationDashboardActionRequiredMessage
                                        declarationParams={declarationExternalEntityParams}
                                    />
                                )}

                            <StyledSegmented
                                options={[
                                    { value: 'active', label: 'Active' },
                                    { value: 'archived', label: 'Archived' },
                                    { value: 'core', label: 'Core Declarations' },
                                ]}
                                value={archived ? 'archived' : appliedParams.isCoreTemplate ? 'core' : 'active'}
                                onChange={(val) => {
                                    if (val === 'archived') {
                                        setArchived(true);
                                        setIsCoreTemplate(undefined);
                                    } else if (val === 'core') {
                                        setArchived(undefined);
                                        setIsCoreTemplate(true);
                                    } else {
                                        setArchived(undefined);
                                        setIsCoreTemplate(undefined);
                                    }
                                    setPage(0);
                                }}
                            />

                            {isEnsDashboard && <ConsultEnsModalButton standalone />}

                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => navigate(`/invoice-upload/${country}/${type}`)}
                            >
                                {createButtonTitle}
                            </Button>
                        </Space>
                    }
                />

                <Row justify="space-between">
                    <Col>
                        <Space size={8}>
                            <SearchBar
                                inputPlaceholder="Search declarations"
                                value={queryInputVal}
                                onSearch={(val) => {
                                    setQueryInputVal(val);
                                    setPage(0);
                                }}
                                onClear={() => {
                                    setQueryInputVal('');
                                    setPage(0);
                                }}
                            />
                            <Button onClick={() => setModal({ type: 'filter' })}>Filter</Button>
                        </Space>
                    </Col>
                    <Col>
                        <Space size={8}>
                            <RangePicker
                                allowClear
                                value={
                                    dates.startDate && dates.endDate
                                        ? [moment(dates.startDate), moment(dates.endDate)]
                                        : undefined
                                }
                                onChange={(values) => {
                                    const [startMoment, endMoment] = values || [];
                                    setDates({
                                        startDate: startMoment
                                            ? startMoment.clone().startOf('day').toISOString()
                                            : undefined,
                                        endDate: endMoment ? endMoment.clone().endOf('day').toISOString() : undefined,
                                    });
                                    setPage(0);
                                }}
                            />
                            {selectedIds.length > 0 && (
                                <Button loading={isExportingCSV} onClick={handleExportDeclarationsCSV}>
                                    Export CSV
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>

                <DeclarationFilterTags tags={tags} onClearTag={clearFilter} onClearAll={clearAll} />

                {declarations && (
                    <DeclarationTable
                        basePath="/declarations"
                        data={declarations as ListPayload<DeclarationAndAgent>}
                        handleDuplicate={canCreateDeclarations ? handleDuplicate : undefined}
                        onUnarchive={canArchiveDeclarations ? handleUnarchive : undefined}
                        onArchive={canArchiveDeclarations ? handleArchive : undefined}
                        onDelete={canDeleteDeclarations ? handleDeleteButtonClick : undefined}
                        loading={isLoadingDeclarations}
                        onPrint={(declarationId: string) => handlePrintButtonClick(declarationId)}
                        onChange={handleTableChange}
                        gvmsReferenceData={gvmsReferenceData}
                        onBarcode={handleOpenBarcodeModal}
                        onCoreTemplateToggle={handleToggleCoreTemplateStatus}
                        groupActions={groupActions}
                        pagination={{
                            current: page + 1,
                            pageSize: size,
                            total: declarations?.total,
                            position: ['bottomCenter'],
                            showSizeChanger: true,
                            hideOnSinglePage: true,
                        }}
                        rowSelection={rowSelection}
                    />
                )}

                <BarcodeModal
                    open={modal.type === 'barcode'}
                    declaration={modal.type === 'barcode' ? modal.declaration : undefined}
                    onCancel={closeModal}
                />
                <DeleteDeclarationModal
                    open={modal.type === 'delete'}
                    onCancel={closeModal}
                    onDelete={handleDeleteDeclaration}
                />
                <ArchiveDeclarationModal
                    open={modal.type === 'archive'}
                    onConfirm={archiveDeclarations}
                    onCancel={closeModal}
                />
                <UnarchiveDeclarationModal
                    open={modal.type === 'unarchive'}
                    onConfirm={unarchiveDeclarations}
                    onCancel={closeModal}
                />
                <DocumentsModal
                    open={modal.type === 'print'}
                    declarationId={modal.type === 'print' ? modal.declarationId : undefined}
                    onCancel={closeModal}
                />
                {groupActionModals}

                <DeclarationFiltersModal
                    open={modal.type === 'filter'}
                    onClose={closeModal}
                    onSubmit={closeModal}
                    onFiltersChange={handleFiltersChange}
                    filters={declarationFilters}
                    onClearAll={clearAll}
                    onClearFilter={clearFilter}
                />
            </Space>
        </StyledPageWrapper>
    );
};

export default DeclarationDashboard;
